const isWrapGeomKind = (geom) => {
  const geomType = String(geom?.kind || geom?.type || "").toLowerCase();
  return geomType === "sphere" || geomType === "cylinder";
};

const buildMaterialNameMap = (materials = {}) => {
  const byName = new Map();
  Object.values(materials || {}).forEach((material) => {
    if (material?.name && !byName.has(material.name)) byName.set(material.name, material);
  });
  return byName;
};

export const resolveMaterialRef = (materialRef, materials = {}, materialNameMap = null) => {
  if (!materialRef) return null;
  if (materials[materialRef]) return materials[materialRef];
  const byName = materialNameMap || buildMaterialNameMap(materials);
  return byName.get(materialRef) || null;
};

export const getGeomMaterial = (geom, materials = {}, materialNameMap = null) => {
  const materialRef = Array.isArray(geom?.materials) ? geom.materials[0] : null;
  return resolveMaterialRef(materialRef, materials, materialNameMap);
};

const addUse = (map, materialUuid, geomKey) => {
  if (!materialUuid || !geomKey) return;
  if (!map.has(materialUuid)) map.set(materialUuid, new Set());
  map.get(materialUuid).add(geomKey);
};

const buildWrapGeomLookup = (bodies = {}, materials = {}, includeMaterials = true) => {
  const byName = new Map();
  const byUuid = new Map();
  const materialNameMap = includeMaterials ? buildMaterialNameMap(materials) : null;

  Object.values(bodies || {}).forEach((body) => {
    (body?.geoms || []).forEach((geom) => {
      if (!body?.uuid || !geom?.uuid) return;
      if (!isWrapGeomKind(geom)) return;
      const key = `${body.uuid}:${geom.uuid}`;
      const material = includeMaterials ? getGeomMaterial(geom, materials, materialNameMap) : null;

      const entry = {
        key,
        body,
        bodyUuid: body.uuid,
        geom,
        geomUuid: geom.uuid,
        material,
        materialUuid: material?.uuid || null,
      };
      const uuidKey = String(geom.uuid);
      if (uuidKey && !byUuid.has(uuidKey)) byUuid.set(uuidKey, entry);
      const nameKey = String(geom.name || "");
      if (nameKey && !byName.has(nameKey)) byName.set(nameKey, entry);
    });
  });

  return { byName, byUuid };
};

const buildWrapGeomIndex = (bodies = {}, materials = {}) => {
  const lookup = buildWrapGeomLookup(bodies, materials, true);
  const allGeomUsesByMaterial = new Map();
  const materialNameMap = buildMaterialNameMap(materials);

  Object.values(bodies || {}).forEach((body) => {
    (body?.geoms || []).forEach((geom) => {
      if (!body?.uuid || !geom?.uuid) return;
      const key = `${body.uuid}:${geom.uuid}`;
      const material = getGeomMaterial(geom, materials, materialNameMap);
      addUse(allGeomUsesByMaterial, material?.uuid, key);
    });
  });

  const { byName, byUuid } = lookup;
  return { byName, byUuid, allGeomUsesByMaterial };
};

const findWrapGeomEntry = (index, ref) => {
  const target = String(ref || "");
  if (!target) return null;
  return index.byUuid.get(target) || index.byName.get(target) || null;
};

export const collectSpatialTendonWrapGeoms = ({ bodies = {}, tendon = null, materials = {} }) => {
  if (!tendon || tendon.kind !== "spatial" || !Array.isArray(tendon.spatial)) return [];

  const index = buildWrapGeomLookup(bodies, materials, true);
  const matches = [];
  const seen = new Set();
  tendon.spatial.forEach((entry, routeIndex) => {
    if (entry?.kind !== "geom" || !entry.geom) return;
    const wrapEntry = findWrapGeomEntry(index, entry.geom);
    if (!wrapEntry || seen.has(wrapEntry.key)) return;
    seen.add(wrapEntry.key);
    matches.push({
      ...wrapEntry,
      routeIndex,
      sidesite: entry.sidesite || "",
    });
  });
  return matches;
};

export const getInternalSpatialTendonWrapMaterialIds = ({
  bodies = {},
  tendons = {},
  materials = {},
}) => {
  const index = buildWrapGeomIndex(bodies, materials);
  const wrapGeomUsesByMaterial = new Map();

  Object.values(tendons || {}).forEach((tendon) => {
    if (!tendon || tendon.kind !== "spatial" || !Array.isArray(tendon.spatial)) return;
    tendon.spatial.forEach((entry) => {
      if (entry?.kind !== "geom" || !entry.geom) return;
      const wrapEntry = findWrapGeomEntry(index, entry.geom);
      addUse(wrapGeomUsesByMaterial, wrapEntry?.materialUuid, wrapEntry?.key);
    });
  });

  const internal = new Set();
  wrapGeomUsesByMaterial.forEach((wrapUses, materialUuid) => {
    const allUses = index.allGeomUsesByMaterial.get(materialUuid);
    if (!allUses || allUses.size !== wrapUses.size) return;
    const onlyWrapUses = [...allUses].every((geomKey) => wrapUses.has(geomKey));
    if (onlyWrapUses) internal.add(materialUuid);
  });
  return internal;
};
